/** @jsxImportSource @emotion/react */
import { EmptyContent, Header } from '@quickbit/backoffice-core-frontend';
import {
  createStyles,
  Icon,
  Loader,
  Table,
  Typography,
  UtilityButton,
  useTheme,
} from '@quickbit/qb-design-system';
import { useNavigate } from 'react-router-dom';
import {
  useCreateSession,
  useDeleteSession,
  useGetSessions,
} from 'api/useAgentChatApi';

const headers = [
  { label: 'Title', cellKey: 'title', sort: false },
  { label: 'Created', cellKey: 'createdAt', sort: false },
  { label: 'Updated', cellKey: 'updatedAt', sort: false },
  { label: 'Actions', cellKey: 'actions', sort: false },
];

export const SessionList = () => {
  const styles = useStyles();
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useGetSessions();
  const createSessionMutation = useCreateSession();
  const deleteSessionMutation = useDeleteSession();

  const handleCreateSession = () => {
    createSessionMutation.mutate(
      { title: '' },
      {
        onSuccess: (response) => {
          void navigate(response.id);
        },
      }
    );
  };

  const handleDeleteSession = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this chat session?')) {
      deleteSessionMutation.mutate({ sessionId });
    }
  };

  const rows =
    data?.sessions.map((session) => ({
      value: {
        id: session.id,
        title: session.title || 'Untitled Session',
        createdAt: new Date(session.createdAt).toLocaleString(),
        updatedAt: new Date(session.updatedAt).toLocaleString(),
        actions: (
          <UtilityButton
            text="Delete"
            variant="text"
            onClick={(e: React.MouseEvent) =>
              handleDeleteSession(e, session.id)
            }
            disabled={deleteSessionMutation.isPending}
          />
        ),
      },
      id: session.id,
    })) ?? [];

  if (isError || (!isLoading && !data)) {
    return (
      <EmptyContent
        heading="Oops! Something went wrong."
        body="Error fetching chat sessions"
        action={
          <UtilityButton
            text="Try again"
            variant="primary"
            onClick={() => void refetch()}
          />
        }
      />
    );
  }

  return (
    <>
      <Header>
        <Typography variant="subtitle">Agent Chat</Typography>
      </Header>
      <div
        css={{
          display: 'flex',
          gap: spacing.xxxs,
          alignItems: 'center',
          marginBottom: spacing.xxs,
        }}
      >
        <UtilityButton
          text="New Chat"
          variant="primary"
          onClick={handleCreateSession}
          disabled={createSessionMutation.isPending}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={() => void refetch()}
          disabled={isLoading}
        />
        {isLoading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.x400 } }}
          />
        )}
      </div>

      {isLoading && (
        <div css={styles.loadingContainer}>
          <Loader />
        </div>
      )}

      {!isLoading && data?.sessions.length === 0 && (
        <EmptyContent
          heading="No chat sessions"
          body="Create a new chat session to get started"
        />
      )}

      {!isLoading && data && data.sessions.length > 0 && (
        <div css={styles.container}>
          <Table
            headers={headers}
            rows={rows}
            onRowClick={(row) => {
              const sessionRow = row as unknown as { id: string };
              void navigate(sessionRow.id);
            }}
          />
        </div>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.m,
  },
}));
